import { useCallback, useMemo } from 'react';
import type {
  DashboardRoute,
  GroupedDashboardRoute,
} from '../Components/Navigation/dashboardRoutes';
import { dashboardRoutes } from '../Components/Navigation/dashboardRoutes';
import { useHasRole } from './useHasRole';
import { useCurrentUser } from './useCurrentUser';
import { MenuKeys } from '../Constants/menuKeys';
import type { MenuProps } from 'antd';
import { Badge, Divider, Grid, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from '../Assets/Styles/Sidebar.module.css';
import {
  useHasLegalDepartmentMemberConventionTasks,
  useHasLegalDepartmentMemberOrganizationTasks,
  useHasOrganizationRepresentativeTasks,
  useHasRectorInternshipTasks,
  useHasRectorOrganizationTasks,
  useHasSupervisorTasks,
  useHasTutorTasks,
} from './BadgeHooks/useHasTasks';
import { useUserOrganizations } from './useOrganizations';

type MenuItem = Required<MenuProps>['items'][number];
const { useBreakpoint } = Grid;

export interface MenuNotification {
  showDot: boolean;
  count: number;
}

export const useAllowedRoutes = () => {
  const hasRole = useHasRole();
  const loggedInUser = useCurrentUser();
  const { data: userOrganizations, isLoading: isLoadingOrganizations } =
    useUserOrganizations(loggedInUser.id);

  return useMemo(() => {
    return dashboardRoutes.filter((route) => {
      if (
        route.excludedRoles &&
        route.excludedRoles.some((role) => hasRole(role))
      ) {
        return false;
      }

      // if exist a rule, apply
      if (route.rule && !route.rule(loggedInUser.email)) {
        return false;
      }

      // if it has role, go next check
      if (route.roles && !route.roles.some((role) => hasRole(role))) {
        return false;
      }

      // unique filters
      if (route.key === MenuKeys.RegisteredOrganizations) {
        if (
          !isLoadingOrganizations &&
          (!userOrganizations || userOrganizations.length === 0)
        ) {
          return false;
        }
      }

      // if it doesn't have a whitelist or blacklist, or if the role is null, then it is a public menu item
      return true;
    });
  }, [hasRole, isLoadingOrganizations, loggedInUser.email, userOrganizations]);
};

export const useSidebarItems = (collapsed: boolean): MenuItem[] => {
  const { t } = useTranslation();
  const allowedRoutes = useAllowedRoutes();
  const screens = useBreakpoint();

  // notification hooks
  const { hasTask: hasTutorTask, taskCount: tutorTaskCount } =
    useHasTutorTasks();
  const {
    hasTask: hasOrganizationRepresentativeTasks,
    taskCount: organizationRepresentativeTaksCount,
  } = useHasOrganizationRepresentativeTasks();
  const {
    hasTask: hasLegalDepartmentMemberConventionTasks,
    taskCount: legalDepartmentMemberConventionTaskCount,
  } = useHasLegalDepartmentMemberConventionTasks();
  const {
    hasTask: hasLegalDepartmentMemberOrganizationTasks,
    taskCount: legalDepartmentMemberOrganizationTaskCount,
  } = useHasLegalDepartmentMemberOrganizationTasks();
  const {
    hasTask: hasRectorOrganizationTasks,
    taskCount: rectorOrganizationTasks,
  } = useHasRectorOrganizationTasks();
  const { hasTask: hasSupervisorTasks, taskCount: superivosrTasksCount } =
    useHasSupervisorTasks();
  const {
    hasTask: hasRectorInternshipTasks,
    taskCount: rectorInternshipTaskCount,
  } = useHasRectorInternshipTasks();

  const getNotificationForRoute = useCallback(
    (routeKey: string): MenuNotification => {
      switch (routeKey) {
        case MenuKeys.Students:
          return { showDot: hasTutorTask, count: tutorTaskCount };
        case MenuKeys.StudentEvaluationSupervisor:
          return { showDot: hasSupervisorTasks, count: superivosrTasksCount };
        case MenuKeys.ManageInternshipsOrganizationRepresentative:
          return {
            showDot: hasOrganizationRepresentativeTasks,
            count: organizationRepresentativeTaksCount,
          };
        case MenuKeys.OrganizationsLegalDepartment:
          return {
            showDot: hasLegalDepartmentMemberOrganizationTasks,
            count: legalDepartmentMemberOrganizationTaskCount,
          };
        case MenuKeys.Conventions:
          return {
            showDot: hasLegalDepartmentMemberConventionTasks,
            count: legalDepartmentMemberConventionTaskCount,
          };
        case MenuKeys.OrganizationsRector:
          return {
            showDot: hasRectorOrganizationTasks,
            count: rectorOrganizationTasks,
          };
        case MenuKeys.ManageInternshipsRector:
          return {
            showDot: hasRectorInternshipTasks,
            count: rectorInternshipTaskCount,
          };
        default:
          return { showDot: false, count: 0 };
      }
    },
    [
      hasLegalDepartmentMemberConventionTasks,
      hasLegalDepartmentMemberOrganizationTasks,
      hasOrganizationRepresentativeTasks,
      hasRectorInternshipTasks,
      hasRectorOrganizationTasks,
      hasSupervisorTasks,
      hasTutorTask,
      legalDepartmentMemberConventionTaskCount,
      legalDepartmentMemberOrganizationTaskCount,
      organizationRepresentativeTaksCount,
      rectorInternshipTaskCount,
      rectorOrganizationTasks,
      superivosrTasksCount,
      tutorTaskCount,
    ]
  );

  return useMemo(() => {
    const createMenuItem = (
      route: DashboardRoute | GroupedDashboardRoute
    ): MenuItem => {
      const translatedText = t(route.labelKey);
      const notification = getNotificationForRoute(route.key);

      return {
        key: route.key,
        icon: (
          <Badge dot={notification.showDot} className={styles.glowingBadge}>
            <span
              className="ant-menu-item-icon"
              style={{ color: 'inherit', fontSize: '26px' }}
            >
              {route.icon}
            </span>
          </Badge>
        ),
        label:
          collapsed && screens.lg ? (
            <Tooltip
              title={translatedText}
              mouseEnterDelay={0.7}
              placement="right"
            >
              {translatedText}
            </Tooltip>
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <span>{translatedText}</span>

              <Badge
                count={notification.count}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.29)',
                  color: 'white',
                  boxShadow: 'none',
                }}
              />
            </div>
          ),
      };
    };

    const unGroupedMenuItems: MenuItem[] = [];

    // create a map to group the same groupKeys
    const groupMap = new Map<string, { label: string; children: MenuItem[] }>();

    for (const route of allowedRoutes) {
      if ('groupKey' in route) {
        // if the map does not contain this group, create it
        if (!groupMap.has(route.groupKey)) {
          groupMap.set(route.groupKey, {
            label: t(route.groupLabel),
            children: [],
          });
        }
        groupMap.get(route.groupKey)?.children.push(createMenuItem(route));
      } else {
        unGroupedMenuItems.push(createMenuItem(route));
      }
    }

    // convert groupMap to MenuItem
    const groupedMenuItems: MenuItem[] = Array.from(groupMap.entries()).map(
      ([groupKey, groupData]) => ({
        key: groupKey,
        type: 'group',
        label: !collapsed ? (
          <span style={{ fontWeight: 'bold' }}>{groupData.label}</span>
        ) : (
          <Divider
            size="small"
            style={{
              borderColor: '#a7aeb5',
              minWidth: 0,
              width: '45px',
              margin: '0 auto',
            }}
          />
        ),
        children: groupData.children,
      })
    );

    const finalMenuItems: MenuItem[] = [];

    if (groupedMenuItems.length > 0) {
      finalMenuItems.push(...groupedMenuItems);
    }

    // insert divider between grouped and ungrouped items
    if (groupedMenuItems.length > 0 && unGroupedMenuItems.length > 0) {
      finalMenuItems.push({
        type: 'divider',
        style: {
          borderColor: 'transparent',
          margin: '20px 0px',
        },
      });
    }

    if (unGroupedMenuItems.length > 0) {
      finalMenuItems.push(...unGroupedMenuItems);
    }

    return finalMenuItems;
  }, [t, collapsed, screens.lg, allowedRoutes, getNotificationForRoute]);
};
